<template>
  <v-data-table
    :headers="thead"
    :items="items"
    :pagination.sync="pagination"
    :total-items="totalCount"
    :rows-per-page-items="rowsPerPageItems"
    :disable-initial-sort="disableInitialSort"
    :hide-actions="hideActions"
  >
    <template v-slot:headers="props">
      <tr>
        <th
          v-for="header in props.headers"
          class="column sortable"
          style="text-align: left;"
          :key="header.text"
          :class="[pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
          @click="changeSort(header.value)"
        >
          {{ header.text }}
          <v-icon small>arrow_upward</v-icon>
        </th>
      </tr>
    </template>
    <template v-slot:items="props">
      <td v-for="{ value, link } in headers" class="data-table-word-break" :key="value">
        <router-link v-if="link" append :to="{ path: `${props.item[linkField]}` }">{{ props.item[value] }}</router-link>
        <span v-else>{{ props.item[value] }}</span>
      </td>

      <td v-if="actions" class="text-xs-right">
        <check-permission
          v-if="roleAddAction"
          joinType="OR"
          entityType="profile"
          :permissions="['USER.ROLE_MANAGE']"
          :entityId="props.item.userId"
        >
          <router-link :to="{ name: roleLinkField, params: { [roleLinkParam]: props.item[editField] } }">
            <v-btn icon flat>
              <v-icon>mdi-sitemap</v-icon>
            </v-btn>
          </router-link>
        </check-permission>

        <check-permission
          v-if="editAction"
          joinType="OR"
          :permissions="editPerm"
          :entityType="entityType"
          :entityId="props.item.id"
        >
          <v-btn icon flat color="primary" @click="$emit('edit', props.item[editField])">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
        </check-permission>

        <check-permission
          v-if="deleteAction && !props.item.forbidDelete"
          joinType="OR"
          :permissions="deletePerm"
          :entityType="entityType"
          :entityId="props.item.id"
        >
          <v-btn icon flat color="error" @click="$emit('delete', props.item[deleteField])">
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </check-permission>
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { debounce } from 'debounce';
import { isOfficial } from '@witkac/kartamiejska-shared';

/**
 * @vuese
 * @group Components/Material
 * Tabelka
 */
export default {
  props: {
    // Obiekty z polami `value` i `text`
    headers: {
      type: Array,
      required: true
    },
    // Lista obiektów do wyświetlenia w tabeli
    // Wyświetlane będą pola z `value` nagłówków
    items: {
      type: Array,
      required: true
    },
    // Całkowita liczba elementów
    totalCount: {
      type: Number,
      required: false
    },
    // Wyszukiwana fraza
    search: {
      type: String,
      required: false
    },
    // Czy wyświetlać kolumnę z akcjami
    actions: {
      type: Boolean,
      required: false,
      default: false
    },
    // Czy wyświetlać ikonę usuwania
    deleteAction: {
      type: Boolean,
      required: false,
      default: true
    },
    // Czy wyświetlać ikonę edycji
    editAction: {
      type: Boolean,
      required: false,
      default: true
    },
    // Parametr dla linku do edycji roli
    roleLinkParam: {
      type: String,
      required: false,
      default: 'id'
    },
    // Czy wyświetlać ikonę dodawania roli
    roleAddAction: {
      type: Boolean,
      required: false,
      default: false
    },
    // Szerokość kolumny z akcjami
    actionsWidth: {
      type: String,
      required: false,
      default: '170px'
    },
    // Wyłączenie sortowania
    disableInitialSort: {
      type: Boolean,
      required: false,
      default: false
    },
    // Ukrywanie akcji
    hideActions: {
      type: Boolean,
      required: false,
      default: false
    },
    // Nazwa pola do linku
    linkField: {
      type: String,
      required: false,
      default: 'id'
    },
    // Nazwa pola do linku edycji ról
    roleLinkField: {
      type: String,
      required: false
    },
    // Nazwa pola dla id do usunięcia
    deleteField: {
      type: String,
      required: false,
      default: 'id'
    },
    // Nazwa pola dla id do edycji
    editField: {
      type: String,
      required: false,
      default: 'id'
    },
    // Uprawnienie do edycji
    editPerm: {
      type: [String, Array],
      required: false
    },
    // Uprawnienie do usunięcia
    deletePerm: {
      type: [String, Array],
      required: false
    },
    // Typ encji
    entityType: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      pagination: {},
      rowsPerPageItems: [10, 25, 50, { text: 'Wszystkie', value: -1 }],
      firstLoad: true
    };
  },
  computed: {
    thead() {
      return this.actions
        ? [...this.headers, { value: 'id', text: ' ', sortable: false, width: this.actionsWidth }]
        : this.headers;
    },
    isOfficial
  },
  watch: {
    pagination: {
      handler() {
        this.firstLoad ? this.initSort() : this.paginationStart();
      },
      deep: true
    },
    search() {
      this.paginationDebounced();
    }
  },
  methods: {
    /**
     * @vuese
     * Wyłącza domyślne sortowania po pierwszej kolumnie
     */
    initSort() {
      this.pagination.sortBy = undefined;
      this.firstLoad = false;
    },
    /**
     * @vuese
     * Emituje event wywołujący paginacje
     */
    paginationStart() {
      this.$emit('pagination', {
        start: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        count: this.pagination.rowsPerPage,
        order: this.getPaginationValue(this.pagination.sortBy),
        search: this.search,
        desc: this.pagination.descending
      });
    },
    /**
     * @vuese
     * Paginacja z debouncem
     */
    paginationDebounced: debounce(function() {
      this.paginationStart();
    }, 500),
    /**
     * @vuese
     * Sprawdza po jakiejś wartości sortować
     */
    getPaginationValue(order) {
      if (order) {
        for (const header of this.headers) {
          if (header.paginationValue && header.value === order) {
            return header.paginationValue;
          }
        }
        return order;
      }
    },
    changeSort(order) {
      if (this.pagination.sortBy === order) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = order;
        this.pagination.descending = false;
      }
    }
  }
};
</script>

<style scoped>
.data-table-word-break {
  white-space: normal;
  word-break: break-all;
}
</style>
